"use client";

import { useLocale } from "next-intl";
import NextJsLink from "next/link";
import { forwardRef } from "react";
import type { ComponentProps } from "react";

import { Link } from "@/i18n/navigation";
import { getLocaleFallbackPath } from "@/lib/locale-availability";
import { LOCALE_PREFIX } from "@/lib/locale-prefix";

export type NextLinkProps = ComponentProps<typeof Link>;

/**
 * Whether a string href points at a page that doesn't exist in the visitor's
 * locale but does in English, so the link should target the English URL
 * instead of a locale-prefixed 404.
 */
function isEnglishOnlyForLocale(href: string, locale: string): boolean {
  if (!href.startsWith("/") || href.startsWith("//")) return false;
  const pathname = href.split(/[?#]/)[0] ?? "";
  const prefix = LOCALE_PREFIX[locale] ?? "";
  return getLocaleFallbackPath(`${prefix}${pathname}`) !== null;
}

/**
 * Link component that automatically handles locale prefixing via next-intl
 * To be used for internal navigation within the website
 */
const NextLink = forwardRef<HTMLAnchorElement, NextLinkProps>(
  ({ href, locale, ...props }, ref) => {
    const currentLocale = useLocale();

    // next-intl would render `/en/...` for an explicit locale override, which
    // costs a redirect through the legacy /en handler. The plain unprefixed
    // URL is the canonical English page.
    if (
      locale === undefined &&
      typeof href === "string" &&
      isEnglishOnlyForLocale(href, currentLocale)
    ) {
      return <NextJsLink href={href} {...props} ref={ref} />;
    }

    return <Link href={href} locale={locale} {...props} ref={ref} />;
  }
);

NextLink.displayName = "NextLink";

export default NextLink;
